import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import {
  OPEN_SOURCE_PACKAGES,
  LICENSES_GENERATED_AT,
  groupByLicense,
} from '@/src/renderer/lib/data/openSourceLicenses'
import { SettingsSection } from '../../primitives'
import './OpenSourcePage.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * "Open-Source-Lizenzen" unter "Hilfe & Info". Führt die Fremdbibliotheken auf,
 * die mit HappyTask ausgeliefert werden.
 *
 * Bewusst NICHT unter "Rechtliches": dort stehen HappyTasks eigene Texte
 * (Impressum, AGB, Datenschutzerklärung), was etwas anderes ist als die
 * Lizenzhinweise fremder Software.
 *
 * Das ist keine Kür: MIT, BSD, ISC und Apache-2.0 verlangen alle, dass
 * Copyright- und Lizenzhinweis mitgeliefert werden, wenn die Software
 * weitergegeben wird — ein Installer ist so eine Weitergabe.
 *
 * Die Daten kommen aus `lib/data/openSourceLicenses.json`, erzeugt von
 * `npm run licenses`. Nicht von Hand pflegen.
 */
export function OpenSourcePage() {
  const { t } = useTranslation()

  // Reine Ableitung aus einem Modul-Konstanten-Array — einmal rechnen reicht.
  const groups = useMemo(() => groupByLicense(), [])

  const openUrl = (url: string) => {
    getPlatformAPI()
      .openExternal(url)
      .catch((err) => console.error('[OpenSource] Failed to open URL:', err))
  }

  return (
    <div className="open-source-page">
      <SettingsSection title={t('settings.section.openSource')}>
        <p className="open-source-page__intro">{t('settings.openSource.intro')}</p>
        <p className="open-source-page__meta">
          {t('settings.openSource.meta', {
            count: OPEN_SOURCE_PACKAGES.length,
            date: LICENSES_GENERATED_AT,
          })}
        </p>
      </SettingsSection>

      {groups.map(({ license, packages }) => (
        <SettingsSection
          key={license}
          title={`${license} · ${packages.length}`}
          className="open-source-page__group"
        >
          <ul className="open-source-page__list">
            {packages.map((pkg) => (
              <li key={pkg.name} className="open-source-page__item">
                <div className="open-source-page__name">
                  <span className="open-source-page__package">{pkg.name}</span>
                  <span className="open-source-page__version">{pkg.version}</span>
                  {pkg.url && (
                    <WithTooltip content={pkg.url}>
                      <button
                        type="button"
                        className="open-source-page__link"
                        onClick={() => openUrl(pkg.url)}
                        aria-label={t('settings.openSource.openHomepage', { name: pkg.name })}
                      >
                        <ExternalLink size={13} strokeWidth={1.8} aria-hidden="true" />
                      </button>
                    </WithTooltip>
                  )}
                </div>
                {pkg.copyright && <p className="open-source-page__copyright">{pkg.copyright}</p>}
              </li>
            ))}
          </ul>
        </SettingsSection>
      ))}
    </div>
  )
}
